import * as React from 'react';

import { cn } from '@/lib/utils';

// panel.tsx — Section « carte à barre de titre » des écrans de pilotage admin (maquettes 2026-09-16).
// Gabarit UNIQUE pour homogénéiser polices et tailles entre sections : titre serif 20px/500 + compteur
// mono optionnel à gauche, mention ou contrôles à droite, corps pleine largeur (tableaux / lignes plates
// à gouttière 24px). Le <section> est nommé par son <h2> (aria-labelledby). Tokens sémantiques uniquement.

export interface PanelProps {
  /** Identifiant du <h2> (nomme la région). */
  readonly headingId: string;
  readonly title: React.ReactNode;
  /** Compteur affiché en mono après le titre (omis si `undefined`). */
  readonly count?: number | undefined;
  /** Mention ou contrôles alignés à droite de la barre de titre. */
  readonly aside?: React.ReactNode | undefined;
  readonly className?: string;
  readonly children: React.ReactNode;
}

export function Panel({
  headingId,
  title,
  count,
  aside,
  className,
  children,
}: PanelProps): React.JSX.Element {
  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        'overflow-hidden rounded-lg border border-border bg-card',
        className,
      )}
    >
      <div className="flex min-h-[4.5rem] flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-border px-6 py-4">
        <h2
          id={headingId}
          className="flex items-baseline gap-3 font-serif text-xl font-medium"
        >
          {title}
          {count !== undefined ? (
            <span className="font-mono text-sm font-normal text-muted-foreground">
              {count}
            </span>
          ) : null}
        </h2>
        {aside !== undefined ? (
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            {aside}
          </div>
        ) : null}
      </div>
      {children}
    </section>
  );
}

/** Pastille de statut décorative (le texte adjacent porte l'information, WCAG 1.4.1). */
export function StatusDot({
  className,
}: {
  readonly className: string;
}): React.JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={cn('inline-block h-2 w-2 shrink-0 rounded-full', className)}
    />
  );
}
